import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useMemo, useReducer } from 'react'
import { MdChevronRight } from 'react-icons/md'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<string, unknown> {
  id: string
  client_name?: string | null
  member_id?: string | null
  medical_plan_name?: string | null
  lastname?: string | null
  firstname?: string | null
  ssn?: string | null
  pattern_names?: string | null
  patterns?: Pattern[] | null
}

interface Pattern {
  ssn?: string | null
  name?: string | null
  lastname?: string | null
  firstname?: string | null
  medical_plan_name?: string | null
}

export function nonNullablePredicate<T> (value: T | null | undefined): value is T {
  return value != null
}

type QueryKey = [
  'admin-records',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string
  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'client_name',
  'member_id',
  'medical_plan_name',
  'firstname',
  'lastname',
  'ssn'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    sort: {
      field: 'client_name',
      direction: 'asc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query
  )
}

export function AdminRecordsPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)
  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  // let appDispatch = useAppDispatch()

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'admin-records',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/warehouse/admin/records?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'client_name',
      header: 'Client',
      isSortable: true
    },
    {
      name: 'member_id',
      header: 'Member ID',
      isSortable: true
    },
    {
      name: 'medical_plan_name',
      header: 'Medical Plan',
      isSortable: true
    },
    {
      name: 'ssn',
      header: 'SSN',
      isSortable: true
    },
    {
      name: 'firstname',
      header: 'First Name',
      isSortable: true
    },
    {
      name: 'lastname',
      header: 'Last Name',
      isSortable: true
    },
    {
      name: 'pattern_names',
      header: 'Pattern Names',
      isSortable: false
    }
  ], [])

  let filters = useMemo(() => [], [])


  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {}, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Admin records</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by client, member ID, medical plan, SSN, first name, last name, or patterns'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        fetchStatus={query.fetchStatus}
        noDataPlaceholder='No records found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, state, setState) => {
          return (
            <ExpandingComponent item={item} />
          )
        }}
      />
    </div>
  )
}

interface ExpandingComponentProps {
  item: Entry
}

function ExpandingComponent ({ item }: ExpandingComponentProps): JSX.Element {
  let [ patternsExpanded, togglePatternsExpanded ] = useReducer((state: boolean) => !state, true)

  return (
    <div className={styles.expandableRowContent}>
      {(item.patterns?.length ?? 0) > 0 && (
        <div className={styles.c}>
          <div className={styles.r}>
            <button
              type='button'
              className={styles.collapseButton}
              onClick={togglePatternsExpanded}
            >
              <span className={styles.sectionHeader}>
                Patterns
              </span>

              <MdChevronRight
                size={20}
                style={{
                  transform: `rotate(${patternsExpanded ? 90 : 0}deg) translateY(1px)`
                }}
              />
            </button>
          </div>

          {patternsExpanded && (
            <div
              className={styles.c}
              style={{ marginTop: '1rem', gap: '1rem' }}
            >
              {item.patterns?.filter(nonNullablePredicate).map((pattern, i, a) => (
                <Fragment key={pattern.name}>
                  <div className={styles.details}>
                    <span className={styles.label}>Name</span>
                    <span className={styles.value}>
                      {pattern.name ?? '—'}
                    </span>

                    <span className={styles.label}>Medical Plan</span>
                    <span className={styles.value}>
                      {pattern.medical_plan_name ?? '—'}
                    </span>

                    <span className={styles.label}>First Name</span>
                    <span className={styles.value}>
                      {pattern.firstname ?? '—'}
                    </span>

                    <span className={styles.label}>Last Name</span>
                    <span className={styles.value}>
                      {pattern.lastname ?? '—'}
                    </span>

                    <span className={styles.label}>SSN</span>
                    <span className={styles.value}>
                      {pattern.ssn ?? '—'}
                    </span>
                  </div>

                  {i < a.length - 1 && (
                    <hr />
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
